import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  type DataTableRenderProps,
} from '@carbon/react';
import classNames from 'classnames';

import styles from './matrix-table.module.scss';

export interface MatrixTableColumn {
  key: string;
  header: string;
}

export interface MatrixTableProps {
  /** One per row, each `id` being the row's index ("0", "1", …). */
  rows: { id: string }[];
  columns: MatrixTableColumn[];
  /** One cell's content, given its column key and its row's index. */
  renderCell: (columnKey: string, rowIndex: number) => React.ReactNode;
  /** Whether a row is highlighted (and marked aria-current), e.g. the
   *  allocation in use. */
  isRowHighlighted?: (rowIndex: number) => boolean;
  className?: string;
}

/**
 * The NRI Matrix popups' table: a header row and one row per allocation
 * method / source, whose cells the caller renders.
 *
 * @param {MatrixTableProps} props - Component props.
 * @return {JSX.Element} The table.
 */
export function MatrixTable({
  rows,
  columns,
  renderCell,
  isRowHighlighted,
  className,
}: MatrixTableProps) {
  return (
    <div className={classNames(styles.wrapper, className)}>
      <DataTable rows={rows} headers={columns}>
        {({
          rows: tableRows,
          headers,
          getTableProps,
          getHeaderProps,
          getRowProps,
        }: DataTableRenderProps<{ id: string }, unknown[]>) => (
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableRows.map((row) => {
                const rowIndex = Number(row.id);
                const highlighted = isRowHighlighted?.(rowIndex) ?? false;
                return (
                  <TableRow
                    {...getRowProps({ row })}
                    key={row.id}
                    className={highlighted ? styles.highlightedRow : undefined}
                    aria-current={highlighted ? 'true' : undefined}
                  >
                    {row.cells.map((cell) => (
                      <TableCell key={cell.id}>
                        {renderCell(cell.info.header, rowIndex)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DataTable>
    </div>
  );
}
